import { useCallback } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import queryString, { ParsedQuery } from 'query-string'
import { linkWithSearch } from 'src/components/PageLayout/Permalink/generatePermalink'

/**
 * The query parameter a show page keeps its selected tab in:
 * `?showTab=<tabKey>`. Deliberately not `tab`: index pages keep their own
 * tabs in `?tab=`, and links between an index and its show pages carry the
 * whole query string, so sharing one name would let each overwrite the other.
 */
export const SHOW_PAGE_TAB_PARAM = 'showTab'

// Links written before show pages settled on `showTab` (bookmarks, shared
// URLs) still carry `?defaultTab=`.
const LEGACY_TAB_PARAM = 'defaultTab'

/**
 * A show page's selected tab, kept in the URL so a reload, a shared link or a
 * return trip lands on it. `tab` is undefined when the URL names none; `Tabs`
 * then shows its first visible tab, and does the same for a key it doesn't
 * have.
 */
export const useShowPageTab = (): {
  tab: string | undefined
  selectTab: (key: string | null) => void
} => {
  const [searchParams] = useSearchParams()
  const { search, hash, state } = useLocation()
  const navigate = useNavigate()
  const tab =
    searchParams.get(SHOW_PAGE_TAB_PARAM) ??
    searchParams.get(LEGACY_TAB_PARAM) ??
    undefined

  const selectTab = useCallback(
    (key: string | null) => {
      if (key === null) return
      // Everything but the tab survives a click: the page's other params (an
      // index's filters and tab, a deep-linked document), the hash (a control
      // reads #policies to show its way back to the policy), and the
      // navigation state (a folder scan's backTo).
      navigate({ search: withShowTab(search, key), hash }, { state })
    },
    [navigate, search, hash, state],
  )

  return { tab, selectTab }
}

/**
 * `search` with its tab set to `key`. Edits the string rather than
 * re-serialising it, so the rest of the query stays exactly as written:
 * URLSearchParams would turn `dismissed[]=false` into `dismissed%5B%5D=false`.
 */
export const withShowTab = (search: string, key: string): string => {
  const kept = search
    .replace(/^\?/, '')
    .split('&')
    .filter((pair) => {
      if (!pair) return false
      const rawName = pair.split('=')[0]
      let name = rawName
      try {
        name = decodeURIComponent(rawName.replace(/\+/g, ' '))
      } catch {
        // A malformed escape isn't one of ours; keep the pair as it is.
      }
      return name !== SHOW_PAGE_TAB_PARAM && name !== LEGACY_TAB_PARAM
    })
  kept.push(`${SHOW_PAGE_TAB_PARAM}=${encodeURIComponent(key)}`)
  return `?${kept.join('&')}`
}

/**
 * The query a show page's permalink carries: the selected tab and nothing
 * else, so a copied link opens on the tab the user is looking at without the
 * filters an index left in the URL. For `generatePermalink`'s `search`.
 */
export const showPageTabQuery = (
  tab: string | undefined,
): Record<string, string> => (tab ? { [SHOW_PAGE_TAB_PARAM]: tab } : {})

/**
 * A show page's breadcrumb back to its index (or to wherever the reader came
 * from). Carries the query like `linkWithSearch` does, so the index's filters
 * and its own `?tab=` survive the round trip, but not this page's tab:
 * carried along, it would follow the reader into the next record they open
 * from the index, which should start on its first tab.
 */
export const backLinkWithSearch = (
  link: string,
  search: ParsedQuery<string> = queryString.parse(window.location.search, {
    arrayFormat: 'bracket',
  }),
): string => {
  const {
    [SHOW_PAGE_TAB_PARAM]: _showTab,
    [LEGACY_TAB_PARAM]: _legacyTab,
    ...rest
  } = search
  return linkWithSearch(link, rest)
}
